import useMutableValue from '@polestar/component-warehouse-react/hooks/useMutableValue';
import { useAnimationControls } from 'framer-motion';
import { useCallback, useMemo } from 'react';
/**
 * Use unique values here, otherwise our motion might start when other variants are using "visible" for example.
 */
export var MotionControlsVariants;
(function (MotionControlsVariants) {
    MotionControlsVariants["enter"] = "cw-motion-enter";
    MotionControlsVariants["exit"] = "cw-motion-exit";
    MotionControlsVariants["visible"] = "cw-motion-visible";
})(MotionControlsVariants || (MotionControlsVariants = {}));
const useMotionControls = (defaultState = MotionControlsVariants.enter) => {
    const currentState = useMutableValue(defaultState);
    const controller = useAnimationControls();
    const handleStart = useCallback((variant) => {
        controller.stop();
        currentState.set(variant);
        return controller.start(variant).then(() => {
            if (currentState.get() === MotionControlsVariants.exit) {
                currentState.set(MotionControlsVariants.enter);
                controller.set(MotionControlsVariants.enter);
            }
        });
    }, [controller, currentState]);
    const handleShow = useCallback(() => handleStart(MotionControlsVariants.visible), [handleStart]);
    const handleHide = useCallback(() => handleStart(currentState.get() === MotionControlsVariants.visible
        ? MotionControlsVariants.exit
        : MotionControlsVariants.enter), [currentState, handleStart]);
    const handleToggle = useCallback(() => handleStart(currentState.get() !== MotionControlsVariants.visible
        ? MotionControlsVariants.visible
        : MotionControlsVariants.exit), [currentState, handleStart]);
    const wrapperController = useMemo(() => ({
        ...controller,
        show: handleShow,
        hide: handleHide,
        toggle: handleToggle,
        get: () => currentState.get(),
        start: (variant) => handleStart(variant),
    }), [controller, handleShow, handleHide, handleToggle, currentState, handleStart]);
    return {
        controls: wrapperController,
        show: handleShow,
        hide: handleHide,
        toggle: handleToggle,
    };
};
export default useMotionControls;
